import { Component, ElementRef, ViewChild, EventEmitter, Input, Output, OnInit, OnDestroy } from "@angular/core";
import {
    ITCIAssetTag,
    ITCIRating,
    ITCIRatingScore,
    RATING_FILTER_SORT,
    TCI_RATINGS_COMPONENT_MODE,
    TCIRatingScore,
    MAX_RATING_COUNT,
    TCIRatingsDisplaySize, TCI_RATING_MODEL, ITCILike
} from "@tci/common";
import * as lodash from "lodash";
import { Subscription } from "rxjs";
import { NgMarketplaceV2Service } from "../../../ng-marketplace-v2/ng-marketplace-v2.service";
import { NgRatingsService } from "./ng-ratings.service";


@Component({
    selector: "ng-ratings",
    templateUrl: "./ng-ratings.component.html",
    styleUrls: ["./ng-ratings.component.less"]
})
export class NgRatingsComponent implements OnInit {
    @Input() mode: TCI_RATINGS_COMPONENT_MODE = TCI_RATINGS_COMPONENT_MODE.LIST_READ_ONLY;
    @Input() displaySize: TCIRatingsDisplaySize = TCIRatingsDisplaySize.SMALL;
    @Input() allRatings: ITCIRating = { r1_count: 0, r2_count: 0, r3_count: 0, r4_count: 0, r5_count: 0};
    @Input() likes: Number = 0;
    @Input() likeStatus = "";
    @Input() userRating: ITCIRatingScore = TCIRatingScore.ZERO;
    @Input() cssClasses: string | string[] = [];
    @Input() showUserRatingStars = false;
    @Input() showUserRatingScore = false;
    @Input() showWeightedAvgScore = false;
    @Input() showTotalRatingCount = false;
    @Input() sortOrder: RATING_FILTER_SORT = RATING_FILTER_SORT.ascending;
    @Input() ratingModel: TCI_RATING_MODEL = TCI_RATING_MODEL.Like;
    @Output() userRatingChanged: EventEmitter<ITCIRatingScore | ITCILike > = new EventEmitter<ITCIRatingScore | ITCILike>();
    /**
     * Implement this to notify the parent to do any subscription or pre-init activity
     * usage: (onBeforeShow)="doBeforeShow()"
     */
    @Output() beforeShow: EventEmitter<ITCIRatingScore|ITCILike> = new EventEmitter<ITCIRatingScore|ITCILike>();
    /**
     * Implement this to notify the parent to do clean up subscription before hiding
     * (onAfterHide)="doAfterHide()"
     */
    @Output() afterHide: EventEmitter<ITCIRatingScore|ITCILike> = new EventEmitter<ITCIRatingScore|ITCILike>();

    @ViewChild("ngRatingRoot") rootElement: ElementRef;

    readonly maxRatingCount = MAX_RATING_COUNT;
    readonly TCI_RATINGS_COMPONENT_MODE = TCI_RATINGS_COMPONENT_MODE;
    readonly TCI_RATING_MODEL = TCI_RATING_MODEL;

    avgRating = 0;
    avgRatingRound = 0;
    allRatingCount = 0;
    userRatingScore = 0;
    stars = [];
    isHalfRating = false;
    integerPart = 0;


    ngOnInit() {
        this.applyCustomCssClasses();

        if (this.userRating) {
            this.userRatingScore = this.userRating.val;
        } else {
            this.userRatingScore = 0;
        }

        switch (this.mode) {
            case TCI_RATINGS_COMPONENT_MODE.DETAIL_READ_ONLY:
                this.avgRating = this.getAvgRating() || 0;
                this.allRatingCount = this.getTotalRatingCount(this.allRatings);
                this.roundOffRating();
                this.stars = Array(this.maxRatingCount).fill(0).map((x, i) => i);
                break;
            case TCI_RATINGS_COMPONENT_MODE.LIST_READ_ONLY:
                this.avgRating = this.getAvgRating() || 0;
                break;
            case TCI_RATINGS_COMPONENT_MODE.DETAIL_EDIT:
                this.stars = Array(this.maxRatingCount).fill(0).map((x, i) => i);
                break;
            default:
                break;
        }
    }


    getAvgRating(): number {
        return TCIRatingScore.calculateWeightedAvgRating(this.allRatings);
    }

    roundOffRating() {
        this.avgRatingRound = Math.round(this.avgRating * 2) / 2; // Roundoff rating to nearest 0.5
        this.isHalfRating = this.avgRatingRound % 1 !== 0;
        this.integerPart = parseInt(this.avgRatingRound.toString(), 10);
    }

    getTotalRatingCount(allRatings: ITCIRating): number {
        return Object.values(allRatings).reduce((prev, curr) => prev + curr, 0);
    }

    editUserRating(rating: number) {
        this.userRatingScore = rating;
        this.userRatingChanged.emit(this.getRatingfromScore(this.userRatingScore));
    }

    applyCustomCssClasses(): void {
        if (lodash.isString(this.cssClasses)) {
            this.cssClasses = this.cssClasses.split(",");
        }

        lodash.forEach(this.cssClasses, (cssClassName: string) => {
            const element = this.rootElement.nativeElement;
            if (!element.classList.contains(cssClassName)) {
                element.classList.add(cssClassName);
            }
        });
    }

    doBeforeShow() {
        this.beforeShow.emit(this.getRatingfromScore(this.userRatingScore));
    }

    doAfterHide() {
        this.afterHide.emit(this.getRatingfromScore(this.userRatingScore));
    }

    getRatingfromScore(rating: number): ITCIRatingScore {
        return TCIRatingScore.getRatingFromNumber(rating);
    }
}
